import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScrollToTop";
import { useAsideMenu } from "../../../../contexts/AsideMenuContext";

import useSuiviClientsKanban from "./hooks/useSuiviClientsKanban";

import SuiviClientsHeader from "./components/SuiviClientsHeader";
import SuiviClientsToolbar from "./components/SuiviClientsToolbar";
import SuiviClientsBoard from "./components/SuiviClientsBoard";
import SuiviClientDetailModal from "./components/SuiviClientDetailModal";
import SuiviClientQuickCreateCard from "./components/SuiviClientQuickCreateCard";
import SuiviClientDeleteModal from "./components/SuiviClientDeleteModal";

export default function SuiviClients() {
  useScrollToTop();

  const { isOpen } = useAsideMenu();
  const kanban = useSuiviClientsKanban();

  const asideWidth = isOpen ? 290 : 92;

  const fullBoardStyle = useMemo(
    () => ({
      width: `calc(100vw - ${asideWidth}px)`,
      marginLeft: `calc(50% - 50vw + ${asideWidth}px)`,
    }),
    [asideWidth],
  );

  return (
    <div
      className="relative min-h-[calc(100vh-76px)] overflow-hidden bg-slate-950 text-slate-900"
      style={fullBoardStyle}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_15%,rgba(250,204,21,0.28),transparent_30%),radial-gradient(circle_at_85%_8%,rgba(45,212,191,0.22),transparent_28%),radial-gradient(circle_at_45%_105%,rgba(244,114,182,0.24),transparent_34%),linear-gradient(135deg,#111827_0%,#3b0764_45%,#7e22ce_100%)]" />
      <div className="absolute inset-x-0 top-0 h-32 bg-white/5 blur-3xl" />
      <div className="absolute inset-x-0 bottom-0 h-72 bg-[radial-gradient(ellipse_at_bottom,rgba(255,255,255,0.16),transparent_68%)]" />

      <div className="relative z-10 flex min-h-[calc(100vh-76px)] flex-col">
        <div className="border-b border-white/10 bg-slate-950/70 shadow-2xl backdrop-blur-xl">
          <SuiviClientsHeader
            board={kanban.board}
            stats={kanban.stats}
            loading={kanban.loading}
            onRefresh={kanban.loadBoard}
            onCreate={() => kanban.setCreateColumn(kanban.columns?.[0] || null)}
          />
        </div>

        <div className="border-b border-white/10 bg-white/[0.08] px-4 py-3 backdrop-blur-xl">
          <SuiviClientsToolbar
            filters={kanban.filters}
            updateFilter={kanban.updateFilter}
            resetFilters={kanban.resetFilters}
            produitOptions={kanban.produitOptions}
            disciplineOptions={kanban.disciplineOptions}
            totalColumns={kanban.columns.length}
            visibleColumns={kanban.filteredColumns.length}
            allCardsExpanded={kanban.allCardsExpanded}
            onToggleAllCards={kanban.toggleAllCards}
          />
        </div>

        <SuiviClientsBoard
          columns={kanban.filteredColumns}
          loading={kanban.loading}
          onDragEnd={kanban.handleDragEnd}
          onOpenAssociation={kanban.openAssociation}
          onCreateCard={kanban.setCreateColumn}
          allCardsExpanded={kanban.allCardsExpanded}
          expandedCardIds={kanban.expandedCardIds}
          onToggleCardExpanded={kanban.toggleCardExpanded}
        />
      </div>

      {kanban.selectedAssociation && (
        <SuiviClientDetailModal
          association={kanban.selectedAssociation}
          columns={kanban.columns}
          productOptions={kanban.produitOptions}
          saving={kanban.saving}
          loading={kanban.detailLoading}
          onClose={() => kanban.setSelectedAssociation(null)}
          onSave={kanban.handleSaveAssociation}
          onAskDelete={kanban.setAssociationToDelete}
        />
      )}

      {kanban.createColumn && (
        <SuiviClientQuickCreateCard
          column={kanban.createColumn}
          productOptions={kanban.produitOptions}
          saving={kanban.saving}
          onClose={() => kanban.setCreateColumn(null)}
          onCreate={kanban.handleCreateAssociation}
        />
      )}

      {kanban.associationToDelete && (
        <SuiviClientDeleteModal
          association={kanban.associationToDelete}
          saving={kanban.saving}
          onClose={() => kanban.setAssociationToDelete(null)}
          onConfirm={kanban.handleConfirmDeleteAssociation}
        />
      )}

      <ToastContainer position="bottom-right" />
    </div>
  );
}